import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { confirmar } from '@/components/ui/confirm-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils/cn';
import {
  cantidadDe,
  formatoCantidad,
  formatoNumero,
  formatoPorcentaje,
} from '@/lib/utils/formato-precio';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { MarcoDeComercia } from '../components/marco-de-comercia';
import { EncabezadoDeComercia } from '../components/encabezado-de-comercia';
import { PasosDeLogistica } from '../components/pasos-de-logistica';
import { EditorDeCarga } from '../components/carga/editor-de-carga';
import { comerciaService } from '../services/comercia-service';
import { useImportacion } from '../store/importacion-store';
import type { TipoDeContenedor } from '../types/comercia.types';
import { kg, m3, medidas, medidasEnMetros, toneladas } from '../utils/formatos';
import { PALLET, recomendarContenedor, resumenDeLaCarga } from '../utils/carga';

/**
 * Comercia › Logística › Carga (maqueta de Melanie): la carga que viene de
 * Productos, el contenedor que conviene, y el acomodo en 3D a escala real. «Ya
 * crea el contenedor, trae los productos, ellos pueden acomodar solitos o se
 * autoacomodan.» Con varios contenedores: «en el uno mete esto y esto, y en el
 * dos esto».
 */
export function CargaPage() {
  return (
    <MarcoDeComercia>
      <Contenido />
    </MarcoDeComercia>
  );
}

function Contenido() {
  const { importacion, aplicar } = useImportacion();
  const { toast } = useToast();
  const [tipos, setTipos] = useState<{
    estandar: TipoDeContenedor[];
    otros: TipoDeContenedor[];
  } | null>(null);
  const [activoId, setActivoId] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [otroAbierto, setOtroAbierto] = useState(false);

  useEffect(() => {
    comerciaService
      .tiposDeContenedor()
      .then(setTipos)
      .catch((e) =>
        toast({
          title: 'No se pudieron leer los contenedores',
          description: mensajeDeError(e, 'Vuelve a intentar.'),
          variant: 'destructive',
        })
      );
  }, [toast]);

  const productos = importacion?.productos ?? [];
  const contenedores = importacion?.contenedores ?? [];
  const activo = contenedores.find((c) => c.id === activoId) ?? contenedores[0] ?? null;
  const resumen = resumenDeLaCarga(productos);
  // La recomendación sólo cambia si cambia la carga (no las posiciones ni los colores).
  const firmaDeLaCarga = productos
    .map(
      (p) =>
        `${p.id}:${p.cantidad}:${p.largoCm}x${p.anchoCm}x${p.altoCm}:${p.pesoUnitario}:${p.fragil}`
    )
    .join('|');
  const recomendacion = useMemo(
    () => (tipos ? recomendarContenedor(productos, tipos.estandar) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tipos, firmaDeLaCarga]
  );

  const correr = async (
    accion: () => Promise<{ importacion: typeof importacion }>,
    exito?: string
  ) => {
    setOcupado(true);
    try {
      const r = await accion();
      aplicar(r);
      if (exito) toast({ title: exito });
      return r;
    } catch (e) {
      toast({
        title: 'No se pudo hacer el cambio',
        description: mensajeDeError(e, 'Vuelve a intentar.'),
        variant: 'destructive',
      });
      return null;
    } finally {
      setOcupado(false);
    }
  };

  /** Elegir un tipo: si no hay contenedor se crea; si hay, cambia el que se está mirando. */
  const elegirTipo = async (tipo: TipoDeContenedor) => {
    if (!activo) {
      const r = await correr(() => comerciaService.agregarContenedor(tipo.id));
      const lista = r?.importacion?.contenedores ?? [];
      const nuevo = lista[lista.length - 1];
      if (nuevo) setActivoId(nuevo.id);
      return;
    }
    if (activo.tipo.id === tipo.id) return;
    if (activo.lineas.length > 0) {
      const ok = await confirmar({
        title: `¿Cambiar a ${tipo.nombre}?`,
        description:
          'Lo acomodado en este contenedor no entra igual en otro tamaño: se vacía y lo vuelves a acomodar (con «Auto-acomodar» es un clic).',
        confirmText: 'Cambiar',
      });
      if (!ok) return;
    }
    await correr(() => comerciaService.cambiarTipo(activo.id, tipo.id));
  };

  const agregarContenedor = async (tipo: TipoDeContenedor) => {
    const r = await correr(
      () => comerciaService.agregarContenedor(tipo.id),
      `${tipo.nombre} agregado`
    );
    const lista = r?.importacion?.contenedores ?? [];
    const nuevo = lista[lista.length - 1];
    if (nuevo) setActivoId(nuevo.id);
  };

  const quitarActivo = async () => {
    if (!activo) return;
    const ok = await confirmar({
      title: '¿Quitar este contenedor?',
      description:
        activo.lineas.length > 0
          ? 'Lo acomodado en él vuelve a quedar sin ubicar.'
          : 'No tiene carga acomodada.',
      confirmText: 'Quitar',
      variant: 'destructive',
    });
    if (!ok) return;
    await correr(() => comerciaService.quitarContenedor(activo.id), 'Contenedor quitado');
    setActivoId(null);
  };

  /** Aplicar la recomendación: deja exactamente esos contenedores (si están vacíos). */
  const usarRecomendado = async () => {
    if (!recomendacion) return;
    const faltan = recomendacion.cantidad - contenedores.length;
    if (
      contenedores.length === 0 ||
      (contenedores.length === 1 && contenedores[0].lineas.length === 0)
    ) {
      if (contenedores.length === 1)
        await correr(() => comerciaService.cambiarTipo(contenedores[0].id, recomendacion.tipo.id));
      for (let i = contenedores.length === 1 ? 1 : 0; i < recomendacion.cantidad; i++) {
        await correr(() => comerciaService.agregarContenedor(recomendacion.tipo.id));
      }
      return;
    }
    if (faltan > 0)
      for (let i = 0; i < faltan; i++)
        await correr(() => comerciaService.agregarContenedor(recomendacion.tipo.id));
  };

  const listo = contenedores.length > 0 && productos.length > 0;
  const sinUbicar = productos.reduce((s, p) => s + Math.max(0, p.cantidad - p.ubicadas), 0);

  return (
    <>
      <EncabezadoDeComercia
        etiqueta="Comercia · Logística"
        titulo="Carga"
        acumulado={
          importacion
            ? {
                total: importacion.resumen.acumulado,
                fob: importacion.resumen.fob,
                moneda: importacion.moneda,
              }
            : { total: 0, fob: 0 }
        }
      />

      <PasosDeLogistica
        actual="carga"
        importacion={importacion}
        siguiente={{
          texto: 'Continuar',
          href: '/comercia/logistica/rutas',
          deshabilitado: !listo,
          aviso: !listo
            ? productos.length === 0
              ? 'Primero agrega productos'
              : 'Elige un contenedor'
            : sinUbicar > 0
              ? `${cantidadDe(sinUbicar, 'bulto queda', 'bultos quedan')} sin ubicar`
              : undefined,
        }}
      />

      {productos.length === 0 ? (
        <section className="rounded-tarjeta border border-dashed bg-card px-6 py-12 text-center">
          <h2 className="font-titulo text-xl font-semibold">Todavía no hay carga</h2>
          <p className="mt-1 text-muted-foreground">
            La carga sale de tus productos: agrégalos primero.
          </p>
          <Link
            to="/comercia/productos"
            className={buttonVariants({ variant: 'degradado', className: 'mt-5 px-6' })}
          >
            Ir a Productos
          </Link>
        </section>
      ) : (
        <>
          {/*
            Las medidas de la maqueta pasadas a pantalla: la captura está al
            125 % (la escala de Windows), así que sus 425 px son 340.
          */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
            <section aria-label="Tu carga" className="rounded-tarjeta border bg-card p-6 shadow-sm">
              <p className="flex flex-wrap items-baseline gap-2">
                <span className="font-titulo text-xl font-semibold">Tu carga</span>
                <span className="text-sm text-muted-foreground">
                  viene de{' '}
                  <Link
                    to="/comercia/productos"
                    className="font-semibold text-primary hover:underline"
                  >
                    Productos
                  </Link>{' '}
                  · {cantidadDe(productos.length, 'producto', 'productos')}
                </span>
              </p>
              <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 xl:grid-cols-5">
                <Dato nombre="Cantidad" valor={cantidadDe(resumen.bultos, 'bulto', 'bultos')} />
                <Dato nombre="Peso" valor={kg(resumen.pesoKg, true)} />
                <Dato nombre="Volumen" valor={m3(resumen.volumenM3)} />
                <Dato
                  nombre="Pallets"
                  valor={formatoCantidad(resumen.pallets)}
                  nota="si se paletiza"
                  titulo={`Pallet de ${PALLET.largoCm} × ${PALLET.anchoCm} cm con hasta ${formatoNumero(PALLET.altoCm / 100, 1)} m de carga`}
                />
                <Dato
                  nombre="Bulto más grande"
                  valor={
                    resumen.mayor
                      ? `${formatoNumero(resumen.mayor.largo)}×${formatoNumero(resumen.mayor.ancho)}×${formatoNumero(resumen.mayor.alto)}`
                      : '—'
                  }
                  nota="cm"
                />
                <Dato
                  nombre="Tipo de carga"
                  valor="General"
                  nota={resumen.fragil ? 'incluye frágil' : 'sin frágiles'}
                />
              </dl>
            </section>

            <section
              aria-label="Contenedor recomendado"
              className="rounded-tarjeta border bg-card p-6 shadow-sm"
            >
              <p className="text-[15px] text-muted-foreground">
                Carga:{' '}
                <strong className="text-foreground">
                  {m3(resumen.volumenM3)} / {toneladas(resumen.pesoKg)}
                </strong>
              </p>
              {recomendacion ? (
                <>
                  <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-orange-700/10 px-3 py-1 text-xs font-bold tracking-[0.08em] text-orange-700">
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    CONTENEDOR RECOMENDADO
                  </p>
                  <p className="mt-2 font-titulo text-[27px] font-semibold leading-tight">
                    {recomendacion.cantidad} × {recomendacion.tipo.nombre}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    usa {formatoPorcentaje(recomendacion.usoVolumen, { decimales: 0 })} del volumen
                    y {formatoPorcentaje(recomendacion.usoPeso, { decimales: 0 })} del peso
                  </p>
                  {!(
                    contenedores.length === recomendacion.cantidad &&
                    contenedores.every((c) => c.tipo.id === recomendacion.tipo.id)
                  ) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3 rounded-full"
                      onClick={() => void usarRecomendado()}
                      disabled={ocupado}
                    >
                      Usar este
                    </Button>
                  )}
                </>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">
                  {tipos
                    ? 'Agrega las medidas de tus productos para recomendarte un contenedor.'
                    : 'Calculando…'}
                </p>
              )}
            </section>
          </div>

          <div
            className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
            role="radiogroup"
            aria-label="Tipo de contenedor"
          >
            {(tipos?.estandar ?? []).map((t) => (
              <TarjetaDeTipo
                key={t.id}
                tipo={t}
                elegido={activo?.tipo.id === t.id}
                recomendado={recomendacion?.tipo.id === t.id}
                deshabilitado={ocupado}
                onElegir={() => void elegirTipo(t)}
              />
            ))}
            <button
              type="button"
              onClick={() => setOtroAbierto(true)}
              className={cn(
                'flex min-h-[92px] items-center justify-center gap-2 rounded-tarjeta border border-dashed bg-card px-5 text-[17px] font-semibold text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                activo &&
                  !activo.tipo.estandar &&
                  'border-2 border-solid border-orange-700 text-foreground'
              )}
            >
              <Plus className="h-5 w-5" aria-hidden="true" />
              {activo && !activo.tipo.estandar ? activo.tipo.nombre : 'Otro contenedor'}
            </button>
          </div>

          {contenedores.length > 0 && (
            <nav
              aria-label="Contenedores de la importación"
              className="flex flex-wrap items-center gap-2"
            >
              <span className="mr-1 text-sm font-semibold text-muted-foreground">
                Contenedores:
              </span>
              {contenedores.map((c, i) => {
                const unidades = c.lineas.reduce((s, l) => s + l.cantidad, 0);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setActivoId(c.id)}
                    aria-current={activo?.id === c.id ? 'true' : undefined}
                    className={cn(
                      'rounded-full border px-4 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      activo?.id === c.id
                        ? 'border-orange-700 bg-orange-700/10 font-semibold text-foreground'
                        : 'bg-card text-muted-foreground hover:bg-accent'
                    )}
                  >
                    {i + 1} · {c.tipo.nombre}
                    <span className="ml-1.5 text-xs text-muted-foreground tabular-nums">
                      {cantidadDe(unidades, 'bulto', 'bultos')}
                    </span>
                  </button>
                );
              })}
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                onClick={() => activo && void agregarContenedor(activo.tipo)}
                disabled={ocupado || !activo}
              >
                {ocupado ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Plus className="h-4 w-4" aria-hidden="true" />
                )}
                Agregar otro {activo ? activo.tipo.nombre : 'contenedor'}
              </Button>
              {contenedores.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-full text-destructive"
                  onClick={() => void quitarActivo()}
                  disabled={ocupado}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  Quitar este
                </Button>
              )}
            </nav>
          )}

          {activo && importacion ? (
            <EditorDeCarga
              key={activo.id}
              importacion={importacion}
              contenedor={activo}
              onActualizada={(imp) => aplicar({ importacion: imp })}
            />
          ) : (
            <section className="rounded-tarjeta border border-dashed bg-card px-6 py-12 text-center text-muted-foreground">
              Elige un tipo de contenedor arriba para empezar a acomodar.
            </section>
          )}
        </>
      )}

      <DialogoOtroContenedor
        abierto={otroAbierto}
        otros={tipos?.otros ?? []}
        onCerrar={() => setOtroAbierto(false)}
        onElegido={async (t) => {
          setOtroAbierto(false);
          setTipos((x) =>
            x && !x.otros.some((o) => o.id === t.id) ? { ...x, otros: [...x.otros, t] } : x
          );
          await elegirTipo(t);
        }}
      />
    </>
  );
}

function Dato({
  nombre,
  valor,
  nota,
  titulo,
}: {
  nombre: string;
  valor: string;
  nota?: string;
  titulo?: string;
}) {
  return (
    <div title={titulo}>
      <dt className="text-sm text-muted-foreground">{nombre}</dt>
      <dd className="mt-0.5 font-titulo text-[18px] font-semibold leading-tight tabular-nums">
        {valor}
      </dd>
      {nota && <dd className="text-xs text-muted-foreground">{nota}</dd>}
    </div>
  );
}

function TarjetaDeTipo({
  tipo,
  elegido,
  recomendado,
  deshabilitado,
  onElegir,
}: {
  tipo: TipoDeContenedor;
  elegido: boolean;
  recomendado: boolean;
  deshabilitado: boolean;
  onElegir: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={elegido}
      disabled={deshabilitado}
      onClick={onElegir}
      className={cn(
        'flex min-h-[92px] flex-col justify-center rounded-tarjeta border bg-card px-5 py-4 text-left shadow-sm transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60',
        elegido ? 'border-2 border-orange-700 shadow-md' : 'hover:border-foreground/30'
      )}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="font-titulo text-lg font-semibold">{tipo.nombre}</span>
        {recomendado && (
          <span className="rounded-full bg-orange-700 px-2.5 py-0.5 text-[11px] font-bold tracking-[0.06em] text-white">
            RECOMENDADO
          </span>
        )}
      </span>
      <span className="mt-1 text-sm text-muted-foreground tabular-nums">
        {formatoNumero(tipo.volumenM3, 1)} m³ · {toneladas(tipo.pesoMaxKg)} ·{' '}
        {medidasEnMetros(tipo.largoCm, tipo.anchoCm, tipo.altoCm)}
      </span>
    </button>
  );
}

/** «+ Otro contenedor»: uno de los que ya tiene la empresa, o uno nuevo con sus medidas. */
function DialogoOtroContenedor({
  abierto,
  otros,
  onCerrar,
  onElegido,
}: {
  abierto: boolean;
  otros: TipoDeContenedor[];
  onCerrar: () => void;
  onElegido: (t: TipoDeContenedor) => void | Promise<void>;
}) {
  const [nombre, setNombre] = useState('');
  const [largo, setLargo] = useState('');
  const [ancho, setAncho] = useState('');
  const [alto, setAlto] = useState('');
  const [peso, setPeso] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const n = (s: string) => Number(s.replace(',', '.'));
  const valido =
    nombre.trim().length >= 2 && n(largo) > 0 && n(ancho) > 0 && n(alto) > 0 && n(peso) > 0;
  /*
    Qué falta, en palabras: el botón apagado sin explicación dejaba a la persona
    adivinando (un «-300» o un «abc» en una medida no decía nada).
  */
  const empezado = [nombre, largo, ancho, alto, peso].some((v) => v.trim() !== '');
  const falta = !empezado
    ? null
    : nombre.trim().length < 2
      ? 'Ponle un nombre.'
      : !(n(largo) > 0 && n(ancho) > 0 && n(alto) > 0)
        ? 'Las tres medidas interiores, en centímetros y mayores que cero.'
        : !(n(peso) > 0)
          ? 'El peso máximo de carga, en kilos y mayor que cero.'
          : null;

  useEffect(() => {
    if (!abierto) return;
    setNombre('');
    setLargo('');
    setAncho('');
    setAlto('');
    setPeso('');
    setError(null);
  }, [abierto]);

  const crear = async () => {
    if (!valido) return;
    setGuardando(true);
    setError(null);
    try {
      const t = await comerciaService.crearTipo({
        nombre: nombre.trim(),
        largoCm: n(largo),
        anchoCm: n(ancho),
        altoCm: n(alto),
        pesoMaxKg: n(peso),
      });
      await onElegido(t);
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo crear el contenedor.'));
    } finally {
      setGuardando(false);
    }
  };

  const campo = (etiqueta: string, valor: string, set: (v: string) => void, sufijo?: string) => (
    <label className="block text-sm">
      <span className="mb-1 block font-semibold">{etiqueta}</span>
      <span className="flex h-11 items-center gap-2 rounded-xl border border-input bg-campo px-3 focus-within:ring-2 focus-within:ring-ring">
        <input
          value={valor}
          onChange={(e) => set(e.target.value)}
          inputMode={sufijo ? 'decimal' : undefined}
          className="min-w-0 flex-1 bg-transparent outline-none"
        />
        {sufijo && <span className="text-muted-foreground">{sufijo}</span>}
      </span>
    </label>
  );

  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && onCerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Otro contenedor</DialogTitle>
          <DialogDescription>
            Uno que ya usaste, o uno nuevo con sus medidas interiores: un refrigerado, un contenedor
            compartido, la caja de un camión, tu almacén…
          </DialogDescription>
        </DialogHeader>
        {otros.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold">Los que ya tienes</p>
            <ul className="max-h-48 space-y-1.5 overflow-y-auto">
              {otros.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => void onElegido(t)}
                    className="flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    <span className="font-semibold">{t.nombre}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatoNumero(t.volumenM3, 1)} m³ · {medidas(t.largoCm, t.anchoCm, t.altoCm)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="space-y-3">
          <p className="text-sm font-semibold">Uno nuevo</p>
          {campo('Nombre', nombre, setNombre)}
          <div className="grid grid-cols-3 gap-3">
            {campo('Largo', largo, setLargo, 'cm')}
            {campo('Ancho', ancho, setAncho, 'cm')}
            {campo('Alto', alto, setAlto, 'cm')}
          </div>
          {campo('Peso máximo de carga', peso, setPeso, 'kg')}
          {falta && <p className="text-sm text-muted-foreground">{falta}</p>}
          {error && <p className="text-sm text-bloqueo">{error}</p>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button variant="degradado" onClick={() => void crear()} disabled={!valido || guardando}>
            {guardando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Crear y usar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
